// Chat del proyecto: reutiliza el runtime de chat web (bridge → chief-agents)
// con un hilo pineado al proyecto. El primer mensaje inyecta el contexto del
// proyecto y enseña al agente a usar el cerebro vía call_skill (brain-ask /
// brain-write) — opción cero-backend: el contexto viaja en el hilo y la
// resunción de sesión del SDK lo conserva.

import { useMemo, useState } from 'react'
import { MessagesSquare, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChatPane } from '@/components/chat/ChatPane'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { useAgents } from '@/contexts/AgentContext'
import { useBrainPages } from '@/hooks/useBrain'
import { useChatThreads, useCreateThread } from '@/hooks/useChatThreads'
import { archiveThread, cancelTurn, getThread, postMessage, type ChatThread } from '@/lib/chat/client'

const THREAD_PREFIX = 'Tarjeta Presente'

// The account list is built LIVE from the brain's company pages — accounts
// added later appear in new threads without touching this file.
function buildPreamble(accountTitles: string[]): string {
  // Bridge caps messages at 8000 chars — bound the account list.
  const shown = accountTitles.slice(0, 30)
  const accounts = shown.length
    ? shown.join(', ') + (accountTitles.length > shown.length ? ` y ${accountTitles.length - shown.length} más` : '')
    : 'las que aparezcan en el cerebro'
  return [
    'CONTEXTO DEL PROYECTO (recuérdalo durante todo este hilo):',
    `Eres el copiloto del proyecto **Tarjeta Presente** — la vertical de orquestación de pagos físicos (card-present) de Yuno que lidera Rasheed. Cuentas del vertical: ${accounts}.`,
    '',
    'El proyecto tiene un CEREBRO (brain_pages, project "tarjeta-presente") con las llamadas de Gong, el pipeline de Salesforce, los decks y las notas. Úsalo así:',
    '- Para RESPONDER preguntas del proyecto: call_skill(function_name="brain-ask", params={"question": "<la pregunta>", "project": "tarjeta-presente"}) — devuelve la respuesta con citas [[slug]] y los huecos. Prefiérelo SIEMPRE antes que tu memoria.',
    '- Para GUARDAR información nueva que el usuario te dé: call_skill(function_name="brain-write", params={"project": "tarjeta-presente", "page_type": "note", "source": "agent", "title": "<título corto>", "content_md": "<contenido fiel>"}).',
    '',
    'Responde con este mensaje solamente: "Listo — copiloto de Tarjeta Presente activo. Pregúntame por las cuentas, las llamadas recientes o lo que necesites del proyecto."',
  ].join('\n')
}

export function ChatTab() {
  const { session } = useAuth()
  const { agents } = useAgents()
  const { data: threads } = useChatThreads()
  const { data: companyPages, isLoading: companiesLoading } = useBrainPages({ pageType: 'company', limit: 100 })
  const createThread = useCreateThread()
  const queryClient = useQueryClient()
  const dropThread = async (token: string, threadId: string) => {
    await archiveThread(token, threadId).catch(() => undefined)
    // useCreateThread already refetched the list with the new thread in it —
    // invalidate so the archived thread doesn't linger in 'Hilos del proyecto'.
    queryClient.invalidateQueries({ queryKey: ['chat-threads'] })
  }
  const [activeThread, setActiveThread] = useState<ChatThread | null>(null)
  const [agentId, setAgentId] = useState<string>('')
  const [creating, setCreating] = useState(false)
  // Hilo en creación: NO debe aparecer clicable en la lista mientras el
  // preámbulo corre (abrirlo a mitad de turno reabre la sesión sin contexto).
  const [creatingThreadId, setCreatingThreadId] = useState<string | null>(null)

  const activeAgents = useMemo(() => agents.filter((a) => a.status === 'active'), [agents])
  const projectThreads = useMemo(
    () => (threads ?? []).filter((t) => (t.title ?? '').startsWith(THREAD_PREFIX) && t.id !== creatingThreadId),
    [threads, creatingThreadId],
  )
  const agentById = useMemo(() => new Map(agents.map((a) => [a.id, a])), [agents])

  const startThread = async () => {
    const agent = activeAgents.find((a) => a.id === agentId) ?? activeAgents[0]
    if (!agent) { toast.error('No hay agentes activos en la org — crea uno en Agentes IA'); return }
    if (!session?.access_token) { toast.error('Sesión expirada — recarga la página'); return }
    setCreating(true)
    let createdThreadId: string | null = null
    try {
      const token = session.access_token
      const thread = await createThread.mutateAsync({
        agentId: agent.id,
        title: `${THREAD_PREFIX} — ${new Date().toISOString().slice(0, 10)}`,
      })
      createdThreadId = thread.id
      setCreatingThreadId(thread.id)
      const { turn_id: preambleTurnId } = await postMessage(token, thread.id, buildPreamble((companyPages ?? []).map((c) => c.title)))
      // Wait for the preamble turn to reach a TERMINAL state before opening
      // the pane: (a) sdk_session_id is only persisted at turn end — a first
      // user message sent mid-turn would start a context-less parallel
      // session; (b) ChatPane replays history on mount, so waiting also makes
      // the agent's confirmation visible. A failed/aborted turn = no context:
      // abort and archive instead of handing over a thread that lies.
      const deadline = Date.now() + 90000
      let terminal: string | null = null
      while (Date.now() < deadline && !terminal) {
        await new Promise((r) => setTimeout(r, 1500))
        try {
          const { thread: fresh, events } = await getThread(token, thread.id, 500)
          const ev = events.find((e) => e.turn_id === preambleTurnId && ['turn_completed', 'turn_aborted', 'turn_paused', 'error'].includes(e.event_type))?.event_type ?? null
          // turn_completed solo cuenta cuando sdk_session_id YA persistió (el
          // bridge lo escribe en un finally() posterior al evento) — sin eso,
          // el primer mensaje del usuario arrancaría una sesión sin contexto.
          if (ev === 'turn_completed' && !fresh.sdk_session_id) continue
          terminal = ev
        } catch { /* transient — keep polling until deadline */ }
      }
      // Timeout counts as failure too: without turn_completed the SDK session
      // id was never persisted, so the very next message would start a
      // context-less parallel session — the exact thing this wait prevents.
      if (terminal !== 'turn_completed') {
        // Timeout: the turn may still be running server-side — cancel it so
        // the archived thread doesn't keep burning tokens in the background.
        if (!terminal) await cancelTurn(token, thread.id, preambleTurnId).catch(() => undefined)
        await dropThread(token, thread.id)
        toast.error(terminal
          ? 'No se pudo inyectar el contexto del proyecto — reintenta en un momento'
          : 'La inyección de contexto tardó demasiado — reintenta en un momento')
        return
      }
      setActiveThread(thread)
    } catch (e) {
      // A thread whose preamble never landed must not linger in the project
      // list masquerading as context-loaded.
      if (createdThreadId && session?.access_token) {
        await dropThread(session.access_token, createdThreadId)
      }
      const err = e as Error & { status?: number; detail?: { error?: string } }
      if (err.status === 404) {
        toast.error('Ese agente ya no existe — recarga la página y elige otro')
      } else if (err.status === 403) {
        toast.error(err.detail?.error === 'feature_disabled'
          ? 'El chat web no está habilitado para esta org (agent_web_chat_enabled)'
          : 'Ese agente no pertenece a esta org — recarga y elige otro')
      } else {
        toast.error(err.message || 'No se pudo crear el hilo')
      }
    } finally {
      setCreating(false)
      setCreatingThreadId(null)
    }
  }

  if (activeThread) {
    const agent = agentById.get(activeThread.agent_id)
    return (
      <div className="flex h-[calc(100vh-220px)] min-h-[420px] flex-col">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {activeThread.title} · {agent?.name ?? 'agente'}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setActiveThread(null)}>
            ← Hilos del proyecto
          </Button>
        </div>
        <ChatPane
          key={activeThread.id}
          thread={activeThread}
          agentName={agent?.name ?? 'Agente'}
          agentRole={agent?.role ?? null}
        />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <Card className="space-y-3 p-5">
        <div className="flex items-center gap-2 font-heading text-base font-semibold">
          <MessagesSquare className="h-4 w-4 text-teal-500" />
          Conversación con contexto del proyecto
        </div>
        <p className="text-sm text-muted-foreground">
          El hilo arranca con el contexto de Tarjeta Presente inyectado: el agente consulta el
          cerebro (brain-ask) antes de responder y puede guardar lo que le digas (brain-write).
        </p>
        <div className="flex items-end gap-2">
          <div className="grid flex-1 gap-1.5">
            <Select value={agentId || activeAgents[0]?.id || ''} onValueChange={setAgentId}>
              <SelectTrigger><SelectValue placeholder="Elegir agente" /></SelectTrigger>
              <SelectContent>
                {activeAgents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name} — {a.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={startThread} disabled={creating || companiesLoading || activeAgents.length === 0}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {creating ? 'Inyectando contexto…' : 'Nueva conversación'}
          </Button>
        </div>
        {activeAgents.length === 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            No hay agentes activos — actívalos en el planeta Agentes IA.
          </p>
        )}
      </Card>

      {projectThreads.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Hilos del proyecto
          </div>
          {projectThreads.map((t) => (
            <Card
              key={t.id}
              className="cursor-pointer p-3 transition-colors hover:bg-muted/50"
              onClick={() => setActiveThread(t)}
            >
              <div className="flex items-center gap-2 text-sm">
                <span className="truncate font-medium">{t.title}</span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {agentById.get(t.agent_id)?.name ?? 'agente'} · {new Date(t.last_message_at).toLocaleDateString()}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
